import React from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { questionsConfigAtom, stepsConfig } from "../formConfigAuto";
import LeadDocuments from "../LeadDocuments/LeadDocuments";
import {
  getQuestionValue,
  formatValue,
  shouldDisplayQuestion,
} from "../leadAutoDataFormatter";
import Control from "../../../../common/Controls/Control";
import useLeadAutoEdit from "./useLeadAutoEdit";
import useEstimatePremium from "./useEstimatePremium";
import useLeadAutoFinalize from "./useLeadAutoFinalize";

const LeadAutoInfosPanel = ({ leadData }) => {
  const [questionsConfig] = useAtom(questionsConfigAtom);
  const productData = leadData?.productdata || {};
  const userData = leadData?.user || {};

  const {
    isEditing,
    isSaving,
    formState,
    startEditing,
    cancelEditing,
    saveChanges,
    commonInputProps,
    getQuestionsForCategory,
  } = useLeadAutoEdit(leadData);

  const isBonusMalusDefault = (questionKey, readValue) => {
    if (questionKey !== "conducteur1_bonus_malus") return false;
    const raw = isEditing ? formState?.[questionKey] : readValue;
    return parseFloat(raw) === 0.75;
  };

  const BonusMalusDefaultAlert = () => (
    <BonusMalusAlert title="Valeur par défaut (0.75) utilisée en l'absence de réponse dans la demande de devis — à vérifier auprès du client.">
      <i className="fa fa-exclamation-triangle mr-1" />
      À vérifier auprès du client
    </BonusMalusAlert>
  );

  // Sinistre déclaré sans responsabilité (demande antérieure à la question, PER-2466) : l'agent de tarification
  // s'arrête sur une donnée manquante tant qu'elle n'est pas renseignée (revue du 06/10).
  const estResponsabiliteManquante = (questionKey, value) =>
    /^sinistre\d+_responsabilite$/.test(questionKey) && (value === null || value === undefined || value === "");

  const ResponsabiliteManquanteAlert = () => (
    <BonusMalusAlert title="L'agent de tarification s'arrête sur une donnée manquante tant que la responsabilité de ce sinistre n'est pas renseignée.">
      <i className="fa fa-exclamation-triangle mr-1" />
      À compléter pour l'agent
    </BonusMalusAlert>
  );

  const { isEstimating, canEstimate, missingFields, estimatePremium } =
    useEstimatePremium(leadData);

  const {
    isFinalized,
    isFinalizing,
    canFinalize,
    missingFields: finalizeMissingFields,
    finalize,
  } = useLeadAutoFinalize(leadData);

  const finalizeButton = isFinalizing
    ? { icon: "fa-spinner fa-spin", label: "Finalisation..." }
    : isFinalized
      ? { icon: "fa-check-circle", label: "Lead déjà finalisé" }
      : { icon: "fa-paper-plane", label: "Finaliser le lead" };

  const finalizeWarning = isFinalized
    ? null
    : isEditing
      ? {
          icon: "fa-info-circle",
          text: "Enregistrez vos modifications avant de finaliser la demande.",
        }
      : finalizeMissingFields.length > 0
        ? {
            icon: "fa-exclamation-triangle",
            text: (
              <>
                <strong>Finalisation impossible</strong> — réponses manquantes :{" "}
                {finalizeMissingFields.map((f) => f.label).join(", ")}
              </>
            ),
          }
        : null;

  // --- Mode lecture : affichage d'une question en lecture seule ---
  const renderQuestionReadOnly = (questionKey, config) => {
    if (!shouldDisplayQuestion(questionKey, config, productData, userData))
      return null;

    const value = getQuestionValue(questionKey, productData, userData);
    const formattedValue = formatValue(questionKey, value, config);

    return (
      <QuestionRow key={questionKey}>
        <QuestionLabel>
          {config.label}
          {isBonusMalusDefault(questionKey, value) && <BonusMalusDefaultAlert />}
          {estResponsabiliteManquante(questionKey, value) && <ResponsabiliteManquanteAlert />}
        </QuestionLabel>
        <QuestionValue isEmpty={value === null || value === undefined}>
          {formattedValue}
        </QuestionValue>
      </QuestionRow>
    );
  };

  // --- Mode édition : affichage d'une question via le composant Control ---
  const renderQuestionEditable = (questionKey, config) => {
    return (
      <EditQuestionWrapper key={questionKey}>
        <Control {...commonInputProps(config)} />
        {isBonusMalusDefault(questionKey) && <BonusMalusDefaultAlert />}
      </EditQuestionWrapper>
    );
  };

  // --- Rendu d'une section de questions ---
  const renderQuestionsSection = (categoryId, questions) => {
    if (isEditing) {
      const editableQuestions = getQuestionsForCategory(categoryId);
      if (editableQuestions.length === 0) return null;
      return (
        <QuestionsGrid>
          {editableQuestions.map(([key, config]) =>
            renderQuestionEditable(key, config),
          )}
        </QuestionsGrid>
      );
    }

    return (
      <QuestionsGrid>
        {questions.map(([key, config]) => renderQuestionReadOnly(key, config))}
      </QuestionsGrid>
    );
  };

  // Étape 2 (PER-2428) : estimée = première étape terminée, finalisée =
  // dossier complet. Une demande estimée depuis plusieurs jours sans
  // finalisation est un prospect à relancer.
  const etatDemande = leadData?.isFinalized
    ? {
        icon: "fa-check-circle",
        color: "#16a34a",
        text: (
          <>
            <strong>Demande finalisée</strong>
            {leadData.finalizedAt &&
              ` le ${new Date(leadData.finalizedAt).toLocaleDateString("fr-FR")}`}{" "}
            : dossier complet, l'agent de tarification peut la traiter.
          </>
        ),
      }
    : leadData?.isEstimated
      ? {
          icon: "fa-hourglass-half",
          color: "#d97706",
          text: (
            <>
              <strong>Demande à finaliser</strong>
              {leadData.estimatedAt &&
                ` depuis le ${new Date(leadData.estimatedAt).toLocaleDateString("fr-FR")}`}{" "}
              : la première étape du formulaire est terminée, la seconde (devis
              prêt à signer) ne l'est pas.
            </>
          ),
        }
      : {
          icon: "fa-pencil",
          color: "#6c757d",
          text: (
            <>
              <strong>Demande commencée</strong> : la première étape du formulaire
              n'est pas terminée.
            </>
          ),
        };

  const renderActions = () => (
    <>
      <EtatDemande color={etatDemande.color}>
        <i className={`fa ${etatDemande.icon} mr-2`} />
        <div>{etatDemande.text}</div>
      </EtatDemande>
      <FinalizeActions>
        <FinalizeButton
          className="btn btn-sm"
          onClick={finalize}
          disabled={!canFinalize || isEditing}
        >
          <i className={`fa ${finalizeButton.icon} mr-1`} />
          {finalizeButton.label}
        </FinalizeButton>

        {finalizeWarning && (
          <MissingFieldsWarning>
            <i className={`fa ${finalizeWarning.icon} mr-2`} />
            <div>{finalizeWarning.text}</div>
          </MissingFieldsWarning>
        )}
      </FinalizeActions>

      <Toolbar>
        {!isEditing ? (
          <ToolbarButton
            className="btn btn-sm btn-link border-0"
            onClick={startEditing}
          >
            <i className="fa fa-pencil mr-1" />
            Modifier les réponses
          </ToolbarButton>
        ) : (
          <>
            <ToolbarButton
              className="btn btn-sm btn-success"
              onClick={saveChanges}
              disabled={isSaving}
            >
              <i
                className={`fa ${isSaving ? "fa-spinner fa-spin" : "fa-check"} mr-1`}
              />
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </ToolbarButton>
            <ToolbarButton
              className="btn btn-sm btn-default"
              onClick={cancelEditing}
              disabled={isSaving}
            >
              <i className="fa fa-times mr-1" />
              Annuler
            </ToolbarButton>
          </>
        )}
      </Toolbar>
    </>
  );

  const questionsWithoutCategory = Object.entries(questionsConfig).filter(
    ([_, config]) => !config.category || config.category === "utilisateur",
  );

  const questionsByStep = stepsConfig.map((step) => ({
    ...step,
    questions: Object.entries(questionsConfig).filter(
      ([_, config]) => config.category === step.id,
    ),
  }));

  return (
    <Container>
      <Section>
        <SectionTitle>
          <i className="fa fa-file-text mr-2" />
          Documents uploadés
        </SectionTitle>
        <LeadDocuments documents={leadData?.documents} userData={userData} />
      </Section>

      <Section>
        <SectionTitle>
          <i className="fa fa-calculator mr-2" />
          Estimation de prime
        </SectionTitle>

        {productData.prime_annuelle_ttc ? (
          <PrimeEstimationCard>
            <PrimeLabel>Prime annuelle TTC estimée</PrimeLabel>
            <PrimeAmount>
              {productData.prime_annuelle_ttc.toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              €
            </PrimeAmount>
            <PrimeMonthly>
              soit{" "}
              {(productData.prime_annuelle_ttc / 12).toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              € /mois
            </PrimeMonthly>
            {(productData.fourchette_prime_annuelle_min ||
              productData.fourchette_prime_annuelle_max) && (
              <FourchetteContainer>
                <FourchetteLabel>Fourchette d'estimation</FourchetteLabel>
                <FourchetteValues>
                  {productData.fourchette_prime_annuelle_min && (
                    <FourchetteItem>
                      <FourchetteItemLabel>
                        <i className="fa fa-arrow-down mr-1" />
                        Min
                      </FourchetteItemLabel>
                      <FourchetteItemValue>
                        {productData.fourchette_prime_annuelle_min.toLocaleString(
                          "fr-FR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}{" "}
                        €
                      </FourchetteItemValue>
                    </FourchetteItem>
                  )}
                  {productData.fourchette_prime_annuelle_max && (
                    <FourchetteItem>
                      <FourchetteItemLabel>
                        <i className="fa fa-arrow-up mr-1" />
                        Max
                      </FourchetteItemLabel>
                      <FourchetteItemValue>
                        {productData.fourchette_prime_annuelle_max.toLocaleString(
                          "fr-FR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}{" "}
                        €
                      </FourchetteItemValue>
                    </FourchetteItem>
                  )}
                </FourchetteValues>
              </FourchetteContainer>
            )}
            <PrimeDisclaimer>
              <i className="fa fa-info-circle mr-2" />
              Cette estimation a été générée par l'IA et nécessite une
              vérification avant validation.
            </PrimeDisclaimer>
          </PrimeEstimationCard>
        ) : (
          <NoPrimeMessage>
            <i className="fa fa-info-circle mr-2" />
            Aucune estimation de prime n'a encore été calculée pour ce lead.
          </NoPrimeMessage>
        )}

        <EstimateActions>
          {canEstimate ? (
            <EstimateButton
              className="btn btn-lg"
              onClick={estimatePremium}
              disabled={isEstimating}
            >
              <i
                className={`fa ${isEstimating ? "fa-spinner fa-spin" : "fa-magic"} mr-1`}
              />
              {isEstimating
                ? "Estimation en cours..."
                : productData.prime_annuelle_ttc
                  ? "Recalculer l'estimation"
                  : "Estimer la prime"}
            </EstimateButton>
          ) : (
            <MissingFieldsWarning>
              <i className="fa fa-exclamation-triangle mr-2" />
              <div>
                <strong>Estimation impossible</strong> — champs manquants :{" "}
                {missingFields.map((f) => f.label).join(", ")}
              </div>
            </MissingFieldsWarning>
          )}
        </EstimateActions>
      </Section>

      {renderActions()}

      {questionsWithoutCategory.length > 0 && (
        <Section>
          <SectionTitle>
            <i className="fa fa-user mr-2" />
            Informations utilisateur
          </SectionTitle>
          {renderQuestionsSection("utilisateur", questionsWithoutCategory)}
        </Section>
      )}

      {questionsByStep.map((step) => {
        if (!isEditing && step.questions.length === 0) return null;

        return (
          <Section key={step.id}>
            <SectionTitle>
              <i className={`fa ${step.icon} mr-2`} />
              {step.title}
            </SectionTitle>
            {renderQuestionsSection(step.id, step.questions)}
          </Section>
        );
      })}

      {renderActions()}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  min-height: 400px;
`;

const Section = styled.div`
  /* background: white; */
  /* border-radius: 8px; */
  padding: 20px;
  margin-bottom: 20px;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;

  i {
    color: #3498db;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  border-radius: 50rem;
`;

const QuestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const EditQuestionWrapper = styled.div`
  .form-group {
    margin-bottom: 0 !important;
  }
`;

const QuestionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QuestionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
`;

const QuestionValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.isEmpty ? "#adb5bd" : "#2c3e50")};
  font-style: ${(props) => (props.isEmpty ? "italic" : "normal")};
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const BonusMalusAlert = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 10rem;
  cursor: help;

  i {
    color: #d97706;
  }
`;

const PrimeEstimationCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  color: white;
  text-align: center;
`;

const PrimeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
`;

const PrimeAmount = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
`;

const PrimeMonthly = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 16px;
`;

const PrimeDisclaimer = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.15);
  padding: 10px 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;

  i {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const FourchetteContainer = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const FourchetteLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  text-align: center;
`;

const FourchetteValues = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 16px;
`;

const FourchetteItem = styled.div`
  flex: 1;
  text-align: center;
`;

const FourchetteItemLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const FourchetteItemValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: white;
`;

const NoPrimeMessage = styled.div`
  font-size: 14px;
  color: #6c757d;
  font-style: italic;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #dee2e6;
  display: flex;
  align-items: center;

  i {
    color: #adb5bd;
  }
`;

const EstimateActions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const EstimateButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: #f100ff;
  color: white;
  border: none;
  border-radius: 50rem;
  padding: 10px 20px;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    color: white;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const EtatDemande = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #2c3e50;
  background: #f8f9fa;
  border-left: 3px solid ${(props) => props.color};
  border-radius: 6px;
  padding: 10px 14px;
  margin: 0 20px 12px;

  i {
    color: ${(props) => props.color};
    margin-top: 2px;
  }
`;

const FinalizeActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding: 0 20px;
`;

const FinalizeButton = styled(EstimateButton)`
  background: #16a34a;
`;

const MissingFieldsWarning = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  width: 100%;

  i {
    color: #d97706;
    margin-top: 2px;
  }
`;

export default LeadAutoInfosPanel;
